import { types, getParent, flow, getRoot } from "mobx-state-tree"

import Router from "next/router"
import buildPersistedChangesStore from "~/stores/models/persistedChanges"
import buildCustomValidationStore from "~/stores/models/customValidation"
import PersistedImage from "~/stores/models/persistedImage"
import * as PlatesService from "~/services/plates"
import { MAX_FILE_SIZE_MB, PARKING_FLOW_TYPE } from "~/utilities/constants"
import * as StringTools from "~/utilities/string"
import * as FileTools from "~/utilities/files"
import PLATE_VALIDATIONS from "~/utilities/plateValidations"
import TRACKING from "~/services/tracking"
import Network from "./Network"

const PLATE_EDIT_SAVE_KEYS = [
  "user",
  "plateNumber",
  "vehicleType",
  "isAutoDispute",
  "isMainPlate",
  "isEnabled",
  "emailNotificationEnabled",
  "smsNotificationEnabled",
  "state",
  "source",
  "plateChannel",
]

const PlateBaseModel = types
  .model("PlateBaseModel", {
    // web app use only
    fakeId: types.optional(types.identifier, StringTools.random),
    requestStatus: types.optional(Network, {}),
    saveStatus: types.optional(Network, {}),
    // actual plate keys/values
    objectId: types.maybeNull(types.string),
    registrationImage: types.maybeNull(PersistedImage),
    parkingPermitImage: types.maybeNull(PersistedImage),
    plateNumber: types.optional(types.string, ""),
    vehicleType: types.maybeNull(types.string),
    isAutoDispute: types.optional(types.boolean, false), // ticket guardian
    isMainPlate: types.maybeNull(types.boolean),
    isEnabled: types.optional(types.boolean, true),
    emailNotificationEnabled: types.optional(types.boolean, true),
    smsNotificationEnabled: types.optional(types.boolean, true),
    updatedAt: types.maybeNull(types.Date),
    createdAt: types.maybeNull(types.Date),
    state: types.maybeNull(types.string),
    source: types.optional(types.string, "dashboard"),
    plateChannel: types.optional(types.string, "regular"),
  })
  .preProcessSnapshot((snapshot) => {
    if (!snapshot) return null
    // this function will run on a snapshot to format it before submitting it to the store.
    const parsedSnapshot = {}
    const keys = Object.keys(snapshot)
    keys.forEach((key) => {
      const input = snapshot[key]
      switch (key) {
        case "parkingPermitImage":
        case "registrationImage":
          if (input && input.url && input.name) {
            parsedSnapshot[key] = {}
            parsedSnapshot[key].persistedPointer = {
              name: input.name,
              url: input.url,
            }
          } else if (
            input &&
            input.persistedPointer &&
            input.persistedPointer.name &&
            input.persistedPointer.url
          ) {
            parsedSnapshot[key] = input
          }
          break
        case "createdAt":
        case "updatedAt":
          parsedSnapshot[key] = input ? new Date(input) : null
          break
        default:
          parsedSnapshot[key] = input
          break
      }
    })

    return parsedSnapshot
  })
  .views((self) => ({
    get sessionToken() {
      return getParent(self, 2).sessionToken
    },
    get isLoading() {
      return self.requestStatus.isLoading || self.saveStatus.isLoading
    },
    get plateNotifications() {
      return !!self.emailNotificationEnabled && !!self.smsNotificationEnabled
    },
  }))
  .actions((self) => ({
    afterCreate() {},
    fetchImages() {
      if (self.parkingPermitImage) {
        self.parkingPermitImage.fetchAlreadyPersistedImage()
      }
      if (self.registrationImage) {
        self.registrationImage.fetchAlreadyPersistedImage()
      }
    },
    searchTickets: flow(function* searchTicketsGen() {
      const root = getRoot(self)
      root.parkingFlow.flow.setEntryType(PARKING_FLOW_TYPE.both)
      root.parkingFlow.searchCriteria.setPlateNumber(self.plateNumber)
      root.parkingFlow.searchCriteria.setPlateState(self.state)
      const result = yield root.parkingFlow.searchCriteria.fetchTickets(
        Router.pathname
      )
      return result
    }),
    setAutoDispute(bool) {
      self.isAutoDispute = bool
    },
    toggleAutoDispute() {
      self.isAutoDispute = !self.isAutoDispute
    },
    togglePlateNotification() {
      if (self.plateNotifications) {
        self.smsNotificationEnabled = false
        self.emailNotificationEnabled = false
      } else {
        self.smsNotificationEnabled = true
        self.emailNotificationEnabled = true
      }
    },
    changeKey(key, value) {
      self[key] = value
    },
    setValue(key, value) {
      if (PLATE_EDIT_SAVE_KEYS.includes(key)) {
        self[key] = value
      }
    },
    togglePreference(key) {
      self[key] = !self[key]
    },
    loadIfNecessary() {
      if (self.requestStatus.isInit) {
        return self.fetch()
      }
      return Promise.resolve()
    },
    handleDocumentImageDrop: flow(function* imageDropGen(acceptedFiles, type) {
      if (!acceptedFiles || !acceptedFiles.length) return false
      const validFiles = FileTools.removeLargeFilesFromArray(
        acceptedFiles,
        MAX_FILE_SIZE_MB
      )

      if (validFiles && validFiles.length) {
        const file = validFiles[0]
        self.changeKey(type, {})
        if (type === "registrationImage") {
          self.registrationImage.markDirty()
          yield self.registrationImage.attachImageFromFile(file)
        }

        if (type === "parkingPermitImage") {
          self.parkingPermitImage.markDirty()
          yield self.parkingPermitImage.attachImageFromFile(file)
        }
      }
      return true
    }),
    removeLocalEvidenceImage(type) {
      self.changeKey(type, null)
    },
    setAllPlateDocuments: flow(function* setPlateDocuments(objectId) {
      if (self.registrationImage && self.registrationImage.isDirty) {
        const registrationData = {
          imageFile: self.registrationImage.base64EncodedImage,
        }
        yield PlatesService.addImage(
          objectId,
          registrationData,
          "registrationImage",
          self.sessionToken
        )
      }

      if (self.parkingPermitImage && self.parkingPermitImage.isDirty) {
        const permitData = {
          imageFile: self.parkingPermitImage.base64EncodedImage,
        }
        yield PlatesService.addImage(
          objectId,
          permitData,
          "parkingPermitImage",
          self.sessionToken
        )
      }
    }),
    userCreate: flow(function* userCreateGen({ currentPath = null } = {}) {
      try {
        const hasErrors = self.validateKeyList([
          "plateNumber",
          "state",
          "smsNotificationEnabled",
          "emailNotificationEnabled",
          "isAutoDispute",
        ])
        if (hasErrors) {
          throw new Error("Please correct plate details.")
        }
        const doesPlateExist = yield self.checkIfPlateExists()
        if (doesPlateExist) {
          throw new Error(
            `Plate ${self.plateNumber} (${self.state}) already exists.`
          )
        }
        if (currentPath && currentPath.includes("account/confirm-info?mlu=")) {
          self.setValue("source", "magic-link-email-job")
        }
        return self.save()
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("Error user add plate", error)
        }
        self.showToast(
          error.message || "Plate not added. Something went wrong",
          "ERROR"
        )
        return false
      }
    }),
    save: flow(function* save() {
      let success = false
      try {
        if (self.saveStatus.isLoading) {
          return true
        }
        self.saveStatus.requestStart()
        const data = self.buildDataUpdateObject(PLATE_EDIT_SAVE_KEYS)
        if (data) {
          const { auth, parkingFlow } = getRoot(self)

          if (!self.sessionToken) {
            data.userEmail = auth.user.email
          }
          // isAutoDispute is true by default on the plate model, but we want it set to false for the pay flow.
          // For new plates not found in DB / existing plates with AD=false: set to false.
          // For existing plates already in DB: don't change anything.
          let existingPlate = false
          if (parkingFlow.flow.isPayFlow) {
            if (auth.isLoggedIn || auth.user.email) {
              existingPlate = yield self.checkIfPlateExists()
            }
            if (!existingPlate || !existingPlate.isAutoDispute) {
              self.isAutoDispute = false
              data.isAutoDispute = false
            }
          }
          if (!existingPlate) {
            const plate = yield PlatesService.createPlate(
              data,
              self.sessionToken
            )
            self.setPlateDetails(plate)
            yield self.setAllPlateDocuments(plate.objectId)
            self.showToast(
              `Plate ${self.plateNumber} (${self.state}) created successfully.`,
              "SUCCESS"
            )
            TRACKING.plate.plateCreated()
          }
          yield auth.blockCheck()
          success = true
        }
        self.saveStatus.requestComplete()
      } catch (error) {
        const errorMessage = typeof error === "string" ? error : error.message
        self.showToast(
          `Issue creating plate ${self.plateNumber} (${self.state}): ${errorMessage}`,
          "ERROR"
        )
        if (process.env.WINIT_ENV !== "test") {
          console.error("error creating a plate", error)
        }
        success = false
        self.saveStatus.requestError(error)
      }
      return success
    }),
    fetch: flow(function* fetch() {
      let success = false
      try {
        if (self.requestStatus.isLoading) {
          return true
        }
        self.requestStatus.requestStart()
        const plate = yield PlatesService.fetchPlate(
          self.objectId,
          self.sessionToken
        )

        self.setPlateDetails(plate)
        success = true
        self.requestStatus.requestComplete()
      } catch (error) {
        self.showToast(`Issue fetching plate: ${error}`, "ERROR")
        if (process.env.WINIT_ENV !== "test") {
          console.error("error fetching a plate", error)
        }
        success = false
        self.requestStatus.requestError(error)
      }
      return success
    }),
    update: flow(function* fetch() {
      let success = false
      try {
        if (self.saveStatus.isLoading) {
          return true
        }
        self.saveStatus.requestStart()
        const data = self.buildDataUpdateObject(PLATE_EDIT_SAVE_KEYS)
        if (data) {
          const plate = yield PlatesService.updatePlate(
            self.objectId,
            data,
            self.sessionToken
          )
          self.setPlateDetails(plate)
          self.showToast(
            `Plate ${self.plateNumber} (${self.state}) updated successfully.`,
            "SUCCESS"
          )
        }
        yield self.setAllPlateDocuments(self.objectId)
        success = true
        self.saveStatus.requestComplete()
      } catch (error) {
        self.showToast(
          `Issue updating plate ${self.plateNumber} (${self.state}): ${error}`,
          "ERROR"
        )
        if (process.env.WINIT_ENV !== "test") {
          console.error("error updating a plate", error)
        }
        success = false
        self.saveStatus.requestError(error)
      }
      return success
    }),
    archive: flow(function* archiveGen() {
      let success = false
      try {
        if (self.saveStatus.isLoading) {
          return true
        }
        self.changeKey("isEnabled", false)
        success = yield self.update()
        if (!success) {
          throw new Error("Plate not archived")
        }
        self.showToast(
          `Plate ${self.plateNumber} (${self.state}) archived successfully.`,
          "SUCCESS"
        )
        const parent = getParent(self, 2)
        if (parent) {
          parent.fetchAll()
        }
      } catch (error) {
        self.restoreLastSavedValue(["isEnabled"])
        self.showToast(
          `Issue archiving plate ${self.plateNumber} (${self.state}): ${error}`,
          "ERROR"
        )
        if (process.env.WINIT_ENV !== "test") {
          console.error("error archiving a plate", error)
        }
        success = false
      }
      return success
    }),
    checkIfPlateExists: flow(function* checkIfPlateExistsGen() {
      try {
        const { auth } = getRoot(self)
        const foundPlate = yield PlatesService.fetchPlateByNumberAndState(
          self.plateNumber,
          self.state,
          self.sessionToken,
          auth.user.email
        )
        return foundPlate
      } catch (error) {
        self.showToast(
          `Could not check if ${self.plateNumber} (${self.state}) exists: ${error}`,
          "ERROR"
        )
        return true
      }
    }),
    setPlateDetails(preferencesObject) {
      self.setLastUpdate(preferencesObject)
      const root = getRoot(self)
      const { isPayNYCFlow } = root.ui
      if (isPayNYCFlow) {
          self.setValue("plateChannel", "pay_nyc")
      }
      Object.keys(preferencesObject).forEach((key) => {
        if ([...PLATE_EDIT_SAVE_KEYS, "objectId"].includes(key)) {
          self[key] = preferencesObject[key]
        }
      })
    },
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type,
      })
    },
  }))

const PersistedChangesStore = buildPersistedChangesStore()

const CustomValidationStore = buildCustomValidationStore({
  validationObject: PLATE_VALIDATIONS,
})

const Plate = types.compose(
  PlateBaseModel,
  PersistedChangesStore,
  CustomValidationStore
)

export default Plate
